/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Focal has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * MANUALLY CHANGE A VARIANT
 * ------------------------------------------------------------------------------------------------------------
 *
 * You may want to manually change the variant, and let the theme automatically adjust all the selectors. To do
 * that, you can get the DOM element of type "<product-variants>", and call the selectVariant method on it with
 * the variant ID.
 *
 * Example:
 *
 * const productVariantElement = document.querySelector('product-variants');
 * productVariantElement.selectVariant(12345);
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */


// js/helper/currency.js
function formatMoney(cents, format = "") {
  if (typeof cents === "string") {
    cents = cents.replace(".", "");
  }
  const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/, formatString = format || window.themeVariables.settings.moneyFormat;
  function defaultTo(value2, defaultValue) {
    return value2 == null || value2 !== value2 ? defaultValue : value2;
  }
  function formatWithDelimiters(number, precision, thousands, decimal) {
    precision = defaultTo(precision, 2);
    thousands = defaultTo(thousands, ",");
    decimal = defaultTo(decimal, ".");
    if (isNaN(number) || number == null) {
      return 0;
    }
    number = (number / 100).toFixed(precision);
    let parts = number.split("."), dollarsAmount = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, "$1" + thousands), centsAmount = parts[1] ? decimal + parts[1] : "";
    return dollarsAmount + centsAmount;
  }
  let value = "";
  switch (formatString.match(placeholderRegex)[1]) {
    case "amount":
      value = formatWithDelimiters(cents, 2);
      break;
    case "amount_no_decimals":
      value = formatWithDelimiters(cents, 0);
      break;
    case "amount_with_space_separator":
      value = formatWithDelimiters(cents, 2, " ", ".");
      break;
    case "amount_with_comma_separator":
      value = formatWithDelimiters(cents, 2, ".", ",");
      break;
    case "amount_with_apostrophe_separator":
      value = formatWithDelimiters(cents, 2, "'", ".");
      break;
    case "amount_no_decimals_with_comma_separator":
      value = formatWithDelimiters(cents, 0, ".", ",");
      break;
    case "amount_no_decimals_with_space_separator":
      value = formatWithDelimiters(cents, 0, " ");
      break;
    case "amount_no_decimals_with_apostrophe_separator":
      value = formatWithDelimiters(cents, 0, "'");
      break;
  }
  if (formatString.indexOf("with_comma_separator") !== -1) {
    return formatString.replace(placeholderRegex, value);
  } else {
    return formatString.replace(placeholderRegex, value);
  }
}
let currencyFormat = window.themeVariables.settings.currencyCodeEnabled ? window.themeVariables.settings.moneyWithCurrencyFormat : window.themeVariables.settings.moneyFormat;


// document.addEventListener('product:rerender', function(event) {
//   console.log('event',event);
// });

document.addEventListener('variant:changed', function(event) {
  
  
  const priceList = this.querySelector('[data-block-type="price-list"]');

  let variant = event.detail.variant; // Gives you access to the whole variant details
  let form = event.target;

  var final_variant_price = variant["price"];
  var final_variant_compare_at_price = variant["compare_at_price"];

  var discount_percentage = (100 - ((final_variant_price * 100) / final_variant_compare_at_price));

  if (priceList && priceList.hasAttribute('has_addons')) {
    console.log('✅ has_addons attribute is present');
    
    var add_on_products_price = 0;
    final_variant_price = variant["price"] + add_on_products_price;
    final_variant_compare_at_price = variant["compare_at_price"] + add_on_products_price;

    discount_percentage = (100 - ((final_variant_price * 100) / final_variant_compare_at_price));
  
    
    $('.add-on-products_input-field:checked').each(function() {
      add_on_products_price = add_on_products_price + parseInt($(this).attr("price"));
      final_variant_price = variant["price"] + add_on_products_price;
      final_variant_compare_at_price = variant["compare_at_price"] + add_on_products_price;
    });

    let productPrices = this.querySelector("[data-product-price-list]"), currencyFormat = window.themeVariables.settings.currencyCodeEnabled ? window.themeVariables.settings.moneyWithCurrencyFormat : window.themeVariables.settings.moneyFormat;
    var stickyProductPrices = $("[data-product-sticky-price-list]");
    var discount_percentage_html = $('.product__info .product-meta__label-list');
    
    if (!productPrices) {
      return;
    }    
    if (!variant) {
      productPrices.style.display = "none";
    } else {
      productPrices.innerHTML = "";
      if (variant["compare_at_price"] > variant["price"]) {
        productPrices.setAttribute('final_compare_at_price',final_variant_compare_at_price);
        productPrices.setAttribute('current_variant_compare_price',variant["compare_at_price"]);
        
        productPrices.innerHTML += `<span class="price price--highlight ${this.priceClass}"><span class="visually-hidden">${window.themeVariables.strings.productSalePrice}</span>${formatMoney(final_variant_price, currencyFormat)}</span>`;
        productPrices.innerHTML += `<span class="price price--compare"><span class="visually-hidden">${window.themeVariables.strings.productRegularPrice}</span>${formatMoney(final_variant_compare_at_price, currencyFormat)}</span>`;

        console.log('discount_percentage',discount_percentage);
        discount_percentage_html.html(`<span class="label label--highlight label--discount">Save ${Math.round(discount_percentage)}%</span>`)
        // stickyProductPrices.html(`<span class="product-sticky-form__price">${formatMoney(final_variant_price, currencyFormat)}</span>`)
        // console.log('if000',stickyProductPrices.html(),final_variant_price)
      } else {
        productPrices.innerHTML += `<span class="price ${this.priceClass}"><span class="visually-hidden">${window.themeVariables.strings.productSalePrice}</span>${formatMoney(final_variant_price, currencyFormat)}</span>`;
        discount_percentage_html.html(` `)
      }
      productPrices.setAttribute('final_price',final_variant_price);
      productPrices.setAttribute('current_variant_price',variant["price"]);
      productPrices.style.display = "";
    }

  }
  if($('.product-meta-available_offers-item-discounted_price').length > 0){
    $('.product-meta-available_offers-item-discounted_price').each(function (index) {
      var descount_percentage = parseInt($(this).attr("descount_percentage"));
      var descounted_price = final_variant_price - (final_variant_price * (descount_percentage / 100));
      console.log('descount_percentage',descount_percentage,descounted_price);
      if(descounted_price > 0){
        $(this).html(formatMoney(descounted_price, currencyFormat))
      }
      // console.log('0000',formatMoney(descounted_price, currencyFormat));
    });
  }
});

function addFreeProduct(event,cart){
  const settings = window.themeVariables?.settings;
  const freeProduct = settings?.freeProduct_1;
  const freeProductId = freeProduct?.variants?.[0]?.id;
  const freeProductMilestone = parseInt(settings?.freeProduct_milestone_1) * 100;
  
  let freeProductIndex = -1;
  
  if (freeProductId != null) {
    cart.items.forEach((item, index) => {
      if (item.variant_id === freeProductId) {
        freeProductIndex = index + 1;
      }
    });
  
    if ((freeProductIndex === -1) && (cart.item_count > 0) && (cart.total_price > freeProductMilestone)) {
      console.log('9iii')
      $.ajax({
        type: 'POST',
        url: '/cart/add.js',
        async: false,
        data: JSON.stringify({
          quantity: 1,
          id: freeProductId,
          properties: { '_personalize': 'free' }
        }),
        dataType: 'json',
        contentType: 'application/json',
        error: function (jqXHR) {
          const response = $.parseJSON(jqXHR.responseText);
          alert(response.description);
        },
        success: function () {
          $.getJSON("/cart.js",function(cart){
            document.documentElement.dispatchEvent(new CustomEvent("cart:refresh", {
              bubbles: true,
              detail: {
                cart: cart
              }
            }));
          });
          if (document.body.classList.contains('template-cart')) {
           location.reload();
          }
          console.log('free product added');
        }
      });
    }
  
    if (cart.total_price <= freeProductMilestone && freeProductIndex !== -1) {
      $.ajax({
        type: 'POST',
        url: '/cart/change.js',
        data: { line: freeProductIndex, quantity: 0 },
        async: false,
        dataType: 'json',
        success: function () {
          $.getJSON("/cart.js",function(cart){
            document.documentElement.dispatchEvent(new CustomEvent("cart:refresh", {
              bubbles: true,
              detail: {
                cart: cart
              }
            }));
          });
          if (document.body.classList.contains('template-cart')) {
           location.reload();
          }
          console.log('free product removed');
        }
      });
    }
  }
  
  if($('#free_products-popup').length > 0 && cart.item_count > 0){
    const free_products_list = window.themeVariables.settings.free_products_list || [];
    const freeProductVariantIds = free_products_list.map(product => product.variants[0].id);
    const freeProduct_milestone_2 = parseInt(window.themeVariables.settings.freeProduct_milestone_2) * 100;
    
    let freeProductInCart = null; // Will hold the matching free product item
    let secondFreeProductIndex = -1;

    // Find free product in cart
    cart.items.forEach((item, index) => {
      if (freeProductVariantIds.includes(item.variant_id)) {
        freeProductInCart = item; // You can also save its index if needed
        secondFreeProductIndex = index + 1;
        console.log('index',index);
      }
    });
    
    if (freeProductInCart) { console.log('Free product already in cart:', freeProductInCart, secondFreeProductIndex); } 
    else { console.log('No free product in cart'); }
    
    // Popup logic
    if ((cart.total_price >= freeProduct_milestone_2) && free_products_list.length && !freeProductInCart) {
      $('#free_products-popup').attr('open', true);
    }
    if ((cart.total_price < freeProduct_milestone_2) && free_products_list.length && freeProductInCart && (cart.item_count > 0)) {
      console.log('demo');
      $.ajax({
        type: 'POST',
        url: '/cart/change.js',
        data: { line: secondFreeProductIndex, quantity: 0 },
        async: false,
        dataType: 'json',
        success: function () {
          $.getJSON("/cart.js",function(cart){
            document.documentElement.dispatchEvent(new CustomEvent("cart:refresh", {
              bubbles: true,
              detail: {
                cart: cart
              }
            }));
          });
          if (document.body.classList.contains('template-cart')) {
           location.reload();
          }
          console.log('free product removed');
        },
     });
    }
  }
}
document.addEventListener('cart:refresh', function(event) {
  console.log('event',event);
  var cart = event.detail.cart; // Get the updated content of the cart
  addFreeProduct(event,cart)
});
if (document.body.classList.contains('template-cart')) {
  $.getJSON("/cart.js", function(cart) {
    addFreeProduct(null,cart);
  });
}
$(document).ready(function(){

  if($('#free_products-popup').length > 0){
    $('body').on('click', '.free_products--modal_product--list_item-add_button', function () {
      $('#free_products-popup').attr('open', false);
      var variant_id = parseInt($(this).attr('variant_id'));
      console.log('variant_id',variant_id);
      $.ajax({
        type: 'POST',
        url: '/cart/add.js',
        async: false,
        data: JSON.stringify({ quantity: 1, id: variant_id, properties: { '_personalize': 'free', '_free_product_type': 'selected' } }),
        dataType: 'json',
        contentType: 'application/json',
        error: function(jqXHR, textStatus, errorThrown){
          var response = $.parseJSON(jqXHR.responseText);
          alert(response.description);
        },
        success: function() {
          $.getJSON("/cart.js",function(cart){
            document.documentElement.dispatchEvent(new CustomEvent("cart:refresh", {
              bubbles: true,
              detail: {
                cart: cart
              }
            }));
          });
          if (document.body.classList.contains('template-cart')) {
           location.reload();
          }
          $('#free_products-popup').attr('open', false);
          console.log('success');
        }
      });
    });
  }

  $('body').on('change', '.add-on-products_input-field', function () {
    const $checkbox = $(this);
    const addOnPrice = parseInt($checkbox.attr("price")) || 0;
  
    const $productPrices = document.querySelector("[data-product-price-list]");
    // const $stickyPrices = document.querySelector("[data-product-sticky-price-list]");
  
    let currentPrice = parseInt($productPrices.getAttribute("final_price")) || 0;
    let currentCompareAt = parseInt($productPrices.getAttribute("final_compare_at_price")) || 0;

    
  
    // Update prices based on checkbox state
    if ($checkbox.prop('checked')) {
      currentPrice += addOnPrice;
      currentCompareAt += addOnPrice;
    } else {
      currentPrice -= addOnPrice;
      currentCompareAt -= addOnPrice;
    }

  
    // Update DOM
    let newPriceHTML = `<span class="price price--large"><span class="visually-hidden">${window.themeVariables.strings.productSalePrice}</span>${formatMoney(currentPrice, currencyFormat)}</span>`;
    
    var discount_percentage = (100 - ((currentPrice * 100) / currentCompareAt));
    var $discount_percentage_html = $('.product__info .product-meta__label-list');
  
    if (currentCompareAt > currentPrice) {
      newPriceHTML = `<span class="price price--highlight price--large"><span class="visually-hidden">${window.themeVariables.strings.productSalePrice}</span>${formatMoney(currentPrice, currencyFormat)}</span>
      <span class="price price--compare"><span class="visually-hidden">${window.themeVariables.strings.productRegularPrice}</span>${formatMoney(currentCompareAt, currencyFormat)}</span>`;

      $discount_percentage_html.html(`<span class="label label--highlight label--discount">Save ${Math.round(discount_percentage)}%</span>`)
      
      // $stickyPrices.innerHTML = `<span class="product-sticky-form__price">${formatMoney(currentPrice, currencyFormat)}</span>`;
    }
  
    $productPrices.innerHTML = newPriceHTML;
    $productPrices.setAttribute("final_price", currentPrice);
    $productPrices.setAttribute("final_compare_at_price", currentCompareAt);

    if($('.product-meta-available_offers-item-discounted_price').length > 0){
      $('.product-meta-available_offers-item-discounted_price').each(function (index) {
        var descount_percentage = parseInt($(this).attr("descount_percentage"));
        var descounted_price = currentPrice - (currentPrice * (descount_percentage / 100));
        if(descounted_price > 0){
          $(this).html(formatMoney(descounted_price, currencyFormat))
        }
        // console.log('0000',formatMoney(descounted_price, currencyFormat));
      });
    }
  });
  
    
  // $('body').on('change','.add-on-products_input-field',function(){
  //   var productPrices = document.querySelector("[data-product-price-list]");
  //   var stickyProductPrices = document.querySelector("[data-product-sticky-price-list]");
    
  //   var variant_price = parseInt(document.querySelector("[data-product-price-list]").getAttribute("price"));
  //   var variant_compare_at_price = parseInt(document.querySelector("[data-product-price-list]").getAttribute("compare_at_price"));
    
  //   var add_on_product_price = parseInt($(this).attr("price"));

  //   var final_variant_price = variant_price;
  //   var final_variant_compare_at_price = variant_compare_at_price;
    
    
    
  //   if($(this).prop('checked') == true){    
  //     final_variant_price = final_variant_price + add_on_product_price;
  //     final_variant_compare_at_price = final_variant_compare_at_price + add_on_product_price;
      
  //     if (final_variant_compare_at_price > final_variant_price) {
  //       productPrices.setAttribute('compare_at_price',final_variant_compare_at_price);
  //       productPrices.innerHTML = `<span class="price price--highlight price--large"><span class="visually-hidden">${window.themeVariables.strings.productSalePrice}</span>${formatMoney(final_variant_price, currencyFormat)}</span>
  //       <span class="price price--compare"><span class="visually-hidden">${window.themeVariables.strings.productRegularPrice}</span>${formatMoney(final_variant_compare_at_price, currencyFormat)}</span>`;

  //       stickyProductPrices.innerHTML = `<span class="product-sticky-form__price">${formatMoney(final_variant_price, currencyFormat)}</span>`
  //     } else {
  //       productPrices.innerHTML = `<span class="price price--large"><span class="visually-hidden">${window.themeVariables.strings.productSalePrice}</span>${formatMoney(final_variant_price, currencyFormat)}</span>`;
  //     }
  //   }else{
  //     console.log('else',final_variant_price,final_variant_compare_at_price);
  //     final_variant_price = final_variant_price - add_on_product_price;
  //     final_variant_compare_at_price = final_variant_compare_at_price - add_on_product_price;
      
  //     if (final_variant_compare_at_price > final_variant_price) {
  //       productPrices.setAttribute('compare_at_price',final_variant_compare_at_price);
  //       productPrices.innerHTML = `<span class="price price--highlight price--large"><span class="visually-hidden">${window.themeVariables.strings.productSalePrice}</span>${formatMoney(final_variant_price, currencyFormat)}</span>
  //       <span class="price price--compare"><span class="visually-hidden">${window.themeVariables.strings.productRegularPrice}</span>${formatMoney(final_variant_compare_at_price, currencyFormat)}</span>`;

  //       stickyProductPrices.innerHTML = `<span class="product-sticky-form__price">${formatMoney(final_variant_price, currencyFormat)}</span>`
  //     } else {
  //       productPrices.innerHTML = `<span class="price price--large"><span class="visually-hidden">${window.themeVariables.strings.productSalePrice}</span>${formatMoney(final_variant_price, currencyFormat)}</span>`;
  //     }
  //   }
  //   productPrices.setAttribute('price',final_variant_price);
  // });

  // if($('').length){
  // }
  if($('.featured-icon_icon-wrapper-main').length > 0){
    $('.featured-icon_icon-wrapper-main').slick({
      speed: 8000,
      autoplay: true,
      autoplaySpeed: 0,
      centerMode: false,
      cssEase: 'linear',
      slidesToShow: 1,
      draggable:false,
      focusOnSelect:false,
      pauseOnFocus:false,
      pauseOnHover:false,
      slidesToScroll: 1,
      variableWidth: true,
      infinite: true,
      initialSlide: 1,
      arrows: false,
      buttons: false
    });
  }
  if($('.action-video_list-wrapper').length > 0){
    $('.action-video_list-wrapper').slick({
      slidesToShow: 5,
      slidesToScroll: 1,
      infinite: false,
      arrows: true,
      responsive: [
        {
          breakpoint: 999,
          settings: {
            slidesToShow: 3,
          }
        },
        {
          breakpoint: 740,
          settings: {
            slidesToShow: 2,
          }
        }
      ]
    });
  }
  if($('.truste_by-icon_list').length > 0){
     $('.truste_by-icon_list').slick({
      slidesToShow: 6,
      slidesToScroll: 1,
      infinite: false,
      arrows: false,
      dots:false,
      responsive: [
        {
          breakpoint: 999,
          settings: {
            slidesToShow: 3,
            dots:true,
          }
        }
      ]
    });
  }

  if($('.featured-product-list_grid').length > 0){
    $('.featured-product-list_grid').slick({
      slidesToShow: 5,
      slidesToScroll: 1,
      infinite: false,
      dots:false,
      responsive: [
        {
          breakpoint: 999,
          settings: {
            slidesToShow: 2
          }
        }
      ]
    });
  }

  if($('.product-recommendations-list__inner').length > 0){
    $('.product-recommendations-list__inner').slick({
      slidesToShow: 5,
      slidesToScroll: 1,
      infinite: false,
      dots:false,
      responsive: [
        {
          breakpoint: 999,
          settings: {
            slidesToShow: 2
          }
        }
      ]
    });
  }

  if($('.shark-tank-reviews_list').length > 0){
    $('.shark-tank-reviews_list').slick({
      slidesToShow: 1,
      slidesToScroll: 1,
      infinite: true,
      arrows: true,
      responsive: [
        {
          breakpoint: 740,
          settings: {
            arrows: false,
            dots: true
          }
        }
      ]
    });
  }

  if($('.occasion-list-grid').length > 0){
    $('.occasion-list-grid').slick({
      slidesToShow: 5,
      slidesToScroll: 1,
      infinite: false,
      arrows: true,
      dots:false,
      responsive: [
        {
          breakpoint: 999,
          settings: {
            slidesToShow: 3,
          }
        },
        {
          breakpoint: 740,
          settings: "unslick"
        }
      ]
    });
  }

  if($('.gushing_item--list').length > 0){
    $('.gushing_item--list').slick({
      slidesToShow: 3,
      slidesToScroll: 1,
      infinite: false,
      arrows: true,
      dots:false,
      responsive: [
        {
          breakpoint: 999,
          settings: {
            slidesToShow: 1,
            arrows: false,
          }
        }
      ]
    });
  }
  if($('.own-logo-designed_image-list').length > 0){
    $('.own-logo-designed_image-list').slick({
      slidesToShow: 1,
      slidesToScroll: 1,
      infinite: false,
      arrows: false,
      dots:true,
      autoplay: true,
      responsive: [
        {
          breakpoint: 999,
          settings: {
            slidesToShow: 1,
            arrows: false,
          }
        }
      ]
    });
  }

  $('body').on('click','.action-video_list-item_ideo', function(){
     if (this.paused) {      
      this.play()
      this.muted = false;
      $(this).parents('.action-video_list-item-wrapper').removeClass('paused');
    } else {
      this.pause()
      this.muted = true;
      $(this).parents('.action-video_list-item-wrapper').addClass('paused');
    }
  });

  $('.faq__load_more--button').click(function() {
    $('.faq__item-on-load_more').toggleClass('active');
    if ($(this).text() == "Load More") {
      $(this).text("Read less")
    } else {
      $(this).text("Load More")
    }
  });
  

  $("body").on("click", ".product-meta-available_offers-item_code-copy_text", function () {
    var copy_text = $(this);
    var text = $(this).attr('code');
    var $temp = $("<input>");
      
    $("body").append($temp);
    $temp.val(text).select();
    document.execCommand("copy");
    $temp.remove();
    copy_text.text("Copied")
      
    setTimeout(function () {
      copy_text.text("Copy");
    }, 1000);
  });

  $('.product-meta-available_offers-header').click(function(){
   $(this).next().slideToggle(); 
  });

  $('a[href^="#"]').on('click',function (e) {
    var target = this.hash,
        $target = $(target);

    $('a[href^="#"]').removeClass('active');
    $(this).addClass('active');
    
    $('html, body').stop().animate({
      'scrollTop': $target.offset().top-190
    }, 900, 'swing', function () {
      window.location.hash = target;
    });
  });
});


// CUSTOMISE PRODUCT FROM JS===============================================================
$(window).on('load', function () {

  // $('.pplr-drop-item').each(function () {
  //   const $el = $(this);
  //   const rawText = $el.text();
    
  //   // Split by pipe and trim each part
  //   const parts = rawText.split('|').map(part => part.trim());
    
  //   // Wrap each part in <span>
  //   const wrapped = parts.map(part => `<span value="${part}">${part}</span>`).join('');
    
  //   // Replace HTML
  //   $el.html(wrapped);
  // });
  $('.pplr-drop-item').each(function () {
    const $el = $(this);
    const rawText = $el.text().trim();
  
    const parts = rawText.split('|').map(part => part.trim());
  
    const wrapped = parts.map((part, index) => {
      let label = part;
  
      if (index === 1) {
        label = `Width: ${part}"`; // 2nd item
      } else if (index === 2) {
        label = `Height: ${part}"`; // 3rd item
      }
  
      return `<span value="${part}">${label}</span>`;
    }).join('');
  
    $el.html(wrapped);
  });

  $('.pplr_checkbox_l').each(function (index) {
    
    const $label = $(this);
    const $checkbox = $label.find('input[type="checkbox"]');

    // Get data
    parseInt($checkbox.data('pplr_price'))
    const labelText = $checkbox.data('value');
    const price = parseInt($checkbox.data('pplr_price')) * 100;

    // Create new structure
    const $wrapper = $('<div class="pplr_checkbox_l-wrapper"></div>');
    $checkbox.appendTo($wrapper);
    $wrapper.append(`<div class="pplr_checkbox_l-info"><span class="pplr_checkbox_l-label">${labelText}</span><span class="pplr_checkbox_l-price">${formatMoney(price, currencyFormat)}</span><button is="toggle-button" class="product-meta--add_on-product_item-info--icon" aria-controls="customise--add-on-product--popup--${index}" aria-expanded="false"><svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M7.00005 12.7143C10.156 12.7143 12.7143 10.1559 12.7143 6.99999C12.7143 3.84408 10.156 1.28571 7.00005 1.28571C3.84414 1.28571 1.28577 3.84408 1.28577 6.99999C1.28577 10.1559 3.84414 12.7143 7.00005 12.7143Z" stroke="currentColor" stroke-width="1.42857" stroke-linecap="round" stroke-linejoin="round"></path><path d="M7 9.28571V7" stroke="currentColor" stroke-width="1.42857" stroke-linecap="round" stroke-linejoin="round"></path><path d="M7 4.71429H7.00664" stroke="currentColor" stroke-width="1.42857" stroke-linecap="round" stroke-linejoin="round"></path></svg></button></div>`);

    // Replace original label text with wrapper
    $label.html($wrapper);
  });

  function debounce(func, delay) {
    let timeout;
    return function () {
      clearTimeout(timeout);
      timeout = setTimeout(() => func.apply(this, arguments), delay);
    };
  }
  
  function replaceMultipleSpaces(input) {
    // Replace any sequence of multiple spaces with a single space
    return input.replace(/\s+/g, '');
  }
  $('textarea.pplr_text.pplr_monogram').on('input', function () {
    const text = $(this).val();

    const lines = text.split('\n');

    var charCount = text.replace(/\s/g, '').length;
    var lineData = text.match(/[^\r\n]+/g) || [''];

    lineData = lineData.map(line => replaceMultipleSpaces(line));

    
    if (lineData.length > 3) {
      $(this).val(validLines.join('\n'));
    }
    $('.pplr-drop-item').each(function () {
      var $spans = $(this).find('span');

      var baseWidth = parseFloat($spans.eq(1).attr('value')) || 0;
      var baseHeight = parseFloat($spans.eq(2).attr('value')) || 0;

      let lineLengths = [
        lineData[0] ? lineData[0].length : 1,
        lineData[1] ? lineData[1].length : 1,
        lineData[2] ? lineData[2].length : 1
      ];
      // console.log('0000000000',Math.max(...lineLengths),lineData,lineData.length,baseWidth,baseHeight);
      let maxLength = Math.max(...lineLengths);
      let updatedWidth = maxLength * baseWidth;
      let updatedHeight = lineData.length * baseHeight;

      const formatValue = (value) => (Number.isInteger(value) ? value : value.toFixed(1));
      
      $spans.eq(1).text(`Width: ${formatValue(updatedWidth)}"`);
      $spans.eq(2).text(`Height: ${formatValue(updatedHeight)}"`);
    });
    
    if(text.length > 0){
      $(this).parents('.product__info').find('.customise-actual--price-list').show();
      $(this).parents('.product__info').find('.customise-zero--price-list').hide();

      $(this).parents('.product__info').find('.product-meta-available_offers-item_title span').show();
    }else{
      $(this).parents('.product__info').find('.customise-actual--price-list').hide();
      $(this).parents('.product__info').find('.customise-zero--price-list').show();

      $(this).parents('.product__info').find('.product-meta-available_offers-item_title span').hide();
    }

    setTimeout(function() {
      let priceText = $('#customise_current_price').text();
      let customise_current_price = parseInt(priceText.replace(/[^\d]/g, ""), 10) * 100;
      
      console.log(customise_current_price);
  
      $('.product-meta-available_offers-item-discounted_price').each(function (index) {
        var _this = this;
        var descount_percentage = parseInt($(_this).attr("descount_percentage"));
        var descounted_price = customise_current_price - (customise_current_price * (descount_percentage / 100));
        // console.log('descounted_price',descounted_price);
        if(descounted_price > 0){
          $(_this).html(formatMoney(descounted_price, currencyFormat))
        }
      });
    }, 2500);
  });

  $('.pplr-drop-item').click(function(){
    let priceText = $('#customise_current_price').text();
    let customise_current_price = parseInt(priceText.replace(/[^\d]/g, ""), 10) * 100;
    
    $('.product-meta-available_offers-item-discounted_price').each(function (index) {
      var descount_percentage = parseInt($(this).attr("descount_percentage"));
      var descounted_price = customise_current_price - (customise_current_price * (descount_percentage / 100));
      if(descounted_price > 0){
        $(this).html(formatMoney(descounted_price, currencyFormat))
      }

      const scrollY = window.scrollY;
      window.scrollTo(0, scrollY);
      // console.log('0000',formatMoney(descounted_price, currencyFormat));
    });
  });

  $('.pplr-selecter-item').click(function(){
    setTimeout(function() {
      let priceText = $('#customise_current_price').text();
      let customise_current_price = parseInt(priceText.replace(/[^\d]/g, ""), 10) * 100;
  
      $('.product-meta-available_offers-item-discounted_price').each(function (index) {
        var descount_percentage = parseInt($(this).attr("descount_percentage"));
        var descounted_price = customise_current_price - (customise_current_price * (descount_percentage / 100));
        if(descounted_price > 0){
          $(this).html(formatMoney(descounted_price, currencyFormat))
        }
        // console.log('0000',formatMoney(descounted_price, currencyFormat));
      });
    }, 1000);
  });
  
  $(document).on('change', 'input[type="checkbox"].pplr-checkbox-item', function() {
    let priceText = $('#customise_current_price').text();
    let customise_current_price = parseInt(priceText.replace(/[^\d]/g, ""), 10) * 100;
    
    $('.product-meta-available_offers-item-discounted_price').each(function (index) {
      var descount_percentage = parseInt($(this).attr("descount_percentage"));
      var descounted_price = customise_current_price - (customise_current_price * (descount_percentage / 100));
      if(descounted_price > 0){
        $(this).html(formatMoney(descounted_price, currencyFormat))
      }
      // console.log('0000',formatMoney(descounted_price, currencyFormat));
    });
  });
});


// $(document).ready(function () {
//     $(document).on("scroll", onScroll);
    
//     //smoothscroll
//     $('a[href^="#"]').on('click', function (e) {
//         e.preventDefault();
//         $(document).off("scroll");
        
//         $('.product-tab-stripe--item_link').each(function () {
//             $(this).removeClass('active');
//         })
//         $(this).addClass('active');
      
//         var target = this.hash,
//             menu = target;
//         $target = $(target);
//         $('html, body').stop().animate({
//             'scrollTop': $target.offset().top+100
//         }, 500, 'swing', function () {
//             window.location.hash = target;
//             $(document).on("scroll", onScroll);
//         });
//     });
// });

// function onScroll(event){
//     var scrollPos = $(document).scrollTop();
//     $('.product-tab-stripe--item_link').each(function () {
//         var currLink = $(this);
//         var refElement = $(currLink.attr("href"));
//         if (refElement.position().top-100 <= scrollPos && refElement.position().top + refElement.height() > scrollPos) {
//             $('.product-tab-stripe--item_link').removeClass("active");
//             currLink.addClass("active");
//         }
//         else{
//             currLink.removeClass("active");
//         }
//     });
// }